import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AllCommunityModule, ModuleRegistry, DateFilterModule } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { toast } from 'react-toastify';
import axiosInstance from '../../lib/axios';
import { Box, Typography, CircularProgress, Tooltip, IconButton, Button, useMediaQuery, useTheme } from '@mui/material';
import { usePermissions } from '../../GlobalFunction/userPermissions';
import { useSelector } from 'react-redux';
import { selectGlobalProperty } from '../../redux/propertySlice';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import HomeIcon from '@mui/icons-material/Home';
import moment from 'moment';

ModuleRegistry.registerModules([AllCommunityModule, DateFilterModule]);

const CheckoutApprovTable = () => {
    const [rowData, setRowData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const UserId = sessionStorage?.getItem('UserId');
    const permissions = usePermissions("Checkout Approval");
    const { AddRec = "0" } = permissions || {};
    const gridRef = useRef();
    const navigate = useNavigate();
    const globalProperty = useSelector(selectGlobalProperty);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const flagStatusMap = {
        'Request Approved': { color: '#ff9800', bgColor: '#fff3e0' },
        'Article Handover': { color: '#4caf50', bgColor: '#e8f5e9' },
        'Account Sattled': { color: '#f44336', bgColor: '#ffebee' },
        'CheckOut Requested': { color: '#2196f3', bgColor: '#e3f2fd' },
        'Cancelled': { color: '#9e9e9e', bgColor: '#f5f5f5' },
    };

    const FlagChipRenderer = (params) => {
        const status = params.value || 'Unknown';
        const config = flagStatusMap[status] || { color: '#757575', bgColor: '#eeeeee' };

        return React.createElement('div', {
            style: {
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '13px',
                fontWeight: '600',
                color: config.color,
                backgroundColor: config.bgColor,
                border: `1px solid ${config.color}40`,
                whiteSpace: 'nowrap',
                minWidth: '60px',
                maxWidth: '100%',
                boxSizing: 'border-box',
                height: '16px',
                lineHeight: '16px',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
            }
        }, status);
    };
    const mobileColDefs = useMemo(() => [
        {
            field: 'RowNum',
            headerName: 'Sr.No',
            width: 60,
            minWidth: 50
        },
        {
            field: 'BookNo',
            headerName: 'Booking No',
            flex: 1,
            minWidth: 100
        },
        {
            field: 'FirstName',
            headerName: 'Resident',
            flex: 1,
            minWidth: 100
        },
        {
            field: 'MobileNo',
            headerName: 'Mobile',
            flex: 1,
            minWidth: 100
        },

        {
            field: 'CheReqDt',
            headerName: 'Request Date',
            flex: 1,
            minWidth: 110,
            valueFormatter: (params) => params.value ? moment(params.value).format('DD-MM-YYYY') : ''
        },
        {
            field: 'Flag',
            headerName: 'Status',
            flex: 1,
            minWidth: 130,
            cellRenderer: FlagChipRenderer,
            cellStyle: { textAlign: 'center' }
        },
    ], []);

    const desktopColDefs = useMemo(() => [
        {
            field: 'RowNum',
            headerName: 'Sr.No',
            width: 60,
            minWidth: 50
        },
        {
            field: 'BookNo',
            headerName: 'Booking No',
            flex: 1,
            minWidth: 100
        },
        {
            field: 'BookDt',
            headerName: 'Book Date',
            flex: 1,
            filter: 'agDateColumnFilter',
            minWidth: 110,
            valueFormatter: (params) => params.value ? moment(params.value).format('DD-MM-YYYY') : ''
        },
        {
            field: 'FirstName',
            headerName: 'Resident',
            flex: 1,
            minWidth: 100
        },
        {
            field: 'MobileNo',
            headerName: 'Mobile',
            flex: 1,
            minWidth: 100
        },
        {
            field: 'RoomBedName',
            headerName: 'Bed No',
            flex: 1,
            minWidth: 100
        },
        {
            field: 'ChkReqByName',
            headerName: 'Requested By',
            flex: 1,
            minWidth: 120
        },

        {
            field: 'PropName',
            headerName: 'Property',
            flex: 1,
            minWidth: 160
        },
        {
            field: 'CheReqDt',
            headerName: 'Request Date',
            flex: 1,
            filter: 'agDateColumnFilter',
            minWidth: 100,
            valueFormatter: (params) => params.value ? moment(params.value).format('DD-MM-YYYY') : ''
        },
        {
            field: 'ExpCheOutDt',
            headerName: 'Checkout Date.',
            flex: 1,
            filter: 'agDateColumnFilter',
            minWidth: 110,
            valueFormatter: (params) => params.value ? moment(params.value).format('DD-MM-YYYY') : ''
        },
        {
            field: 'Remark',
            headerName: 'Remark',
            flex: 1,
            minWidth: 130,
            tooltipValueGetter: (params) => params.value ?? '',
        },
        {
            field: 'Flag',
            headerName: 'Status',
            flex: 1,
            filter: 'agTextColumnFilter',
            minWidth: 150,
            cellRenderer: FlagChipRenderer,
            cellStyle: { textAlign: 'center' }
        },
    ], []);

    const colDefs = isMobile ? mobileColDefs : desktopColDefs;

    const defaultColDef = useMemo(() => ({
        sortable: true,
        filter: !isMobile,
        resizable: true,
        floatingFilter: !isMobile,
        width: 100,
        minWidth: 80,
        tooltipValueGetter: (params) => params.value ?? '',
        wrapHeaderText: true,
        autoHeaderHeight: true,
    }), [isMobile]);

    useEffect(() => {
        fetchCheckout()
    }, [globalProperty]);

    const fetchCheckout = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.post(`TrnBooking/GettrnChkReqDashBoard`,
                {
                    "start": 1,
                    "PageSize": 1,
                    "SearchText": ""
                }
            );

            if (response.data.Status === 0) {
                const formattedData = response.data.Data.map(bconfirm => ({
                    ...bconfirm,
                    BookDt: bconfirm.BookDt ? new Date(bconfirm.BookDt) : null,
                    BookDtFrom: bconfirm.BookDtFrom ? new Date(bconfirm.BookDtFrom) : null,
                    BookUpToDt: bconfirm.BookUpToDt ? new Date(bconfirm.BookUpToDt) : null,
                    CheReqDt: bconfirm.CheReqDt ? new Date(bconfirm.CheReqDt) : null,
                    ExpCheOutDt: bconfirm.ExpCheOutDt ? new Date(bconfirm.ExpCheOutDt) : null,
                    BookSt: bconfirm.BookSt === "C" ? "Confirmed" : bconfirm.BookSt === "B" ? "Booked" : ""
                }));
                setRowData(formattedData);
            }
        } catch (error) {
            toast.error('Error fetching check-out', error);
        } finally {
            setIsLoading(false);
        }
    };

    const onGridReady = useCallback((params) => {
        if (isMobile) {
            setTimeout(() => {
                params.api.sizeColumnsToFit();
            }, 100);
        }
    }, [isMobile]);

    const onGridSizeChanged = useCallback((params) => {
        if (isMobile) {
            params.api.sizeColumnsToFit();
        }
    }, [isMobile]);

    const handleRowDoubleClick = useCallback((params) => {
        const bookId = params.data.BookId;
        const ChkReqId = params.data.ChkReqId;
        navigate('/checkout/checkoutapprovel', { state: { BookId: bookId, ChkReqId: ChkReqId, mode: 'view' } });
    }, [navigate]);

    const handlePermittedClick = () => {
        if (AddRec === "1") {
            navigate('/checkout/checkoutapprovel');
        }
    };

    const handleClearFilters = () => {
        gridRef.current?.api?.setFilterModel(null);
    };

    return (
        <Box
            sx={{
                width: '100%',
                height: '90vh',
                padding: { xs: '5px', sm: '10px' },
                backgroundColor: 'rgb(249, 249, 249)',
                position: 'relative'
            }}
        >
            <Box
                sx={{
                    backgroundColor: "#ffffff",
                    padding: { xs: "8px 12px", sm: "12px 16px" },
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                    borderRadius: "12px",
                    margin: { xs: "5px auto", sm: "10px auto" },
                    maxWidth: "95%",
                    flexDirection: { xs: "column", sm: "column", md: "row" },
                    gap: 2,
                }}
            >

                <Breadcrumbs
                    aria-label="breadcrumb"
                    separator="›"
                    sx={{
                        fontSize: { xs: "12px", sm: "14px", md: "16px" },
                        display: "flex",
                        flexDirection: { xs: "column", sm: "column", md: "row" }
                    }}
                >
                    <Link
                        underline="hover"
                        color="#635bff"
                        sx={{
                            cursor: "pointer",
                            fontWeight: "500",
                            display: "flex",
                            alignItems: "center",
                            fontSize: 'inherit'
                        }}
                        onClick={() => navigate('/dashboard')}
                    >
                        <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                        <Box component="span">Home</Box>
                    </Link>

                    <Link
                        underline="hover"
                        color="#635bff"
                        sx={{
                            cursor: "pointer",
                            fontWeight: "500",
                            fontSize: 'inherit'
                        }}
                        onClick={() => navigate('/checkouttab')}
                    >
                        Checkout & handover
                    </Link>

                    <Typography color="text.primary" sx={{ fontWeight: "500", fontSize: 'inherit' }}>
                        Checkout Approvel Request
                    </Typography>
                </Breadcrumbs>

                {/* Buttons container */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: { xs: 1, sm: 1, md: 0 },
                        flexShrink: 0,
                        flexWrap: 'wrap',
                        justifyContent: { xs: 'center', sm: 'flex-end' }
                    }}
                >
                    <Tooltip title='Clear all applied filter' arrow>
                        <Button
                            variant="outlined"
                            onClick={handleClearFilters}
                            size="small"
                            sx={{
                                borderRadius: "50px",
                                textTransform: "none",
                                borderColor: "#635bff",
                                color: "#635bff",
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                minWidth: 'auto',
                                px: { xs: 2, sm: 3 },
                                "&:hover": {
                                    borderColor: "#524ccc",
                                    backgroundColor: "rgba(99, 91, 255, 0.04)",
                                }
                            }}
                        >
                            {isMobile ? 'Clear' : 'Clear Filters'}
                        </Button>
                    </Tooltip>

                    {/* <Tooltip
                        title={AddRec !== "1" ? "You have no permission to add record" : "Add New Record"}
                        placement="top"
                        arrow
                    >
                        <span>
                            <Button
                                variant="contained"
                                onClick={handlePermittedClick}
                                size="small"
                                disabled={AddRec !== "1"}
                                sx={{
                                    background: AddRec === "1" ? "#635bff" : "rgba(0, 0, 0, 0.12)",
                                    color: AddRec === "1" ? "white" : "rgba(0, 0, 0, 0.26)",
                                    borderRadius: "50px",
                                    padding: { xs: "4px 12px", sm: "6px 15px" },
                                    boxShadow: AddRec === "1" ? "0px 6px 15px rgba(0, 0, 0, 0.2)" : "none",
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    minWidth: 'auto',
                                    "&:hover": {
                                        background: AddRec === "1" ? "#524ccc" : "rgba(0, 0, 0, 0.12)"
                                    }
                                }}
                            >
                                <AddIcon fontSize="small" sx={{ mr: 0.5 }} /> 
                                {isMobile ? '' : 'New'}
                            </Button>
                        </span>
                    </Tooltip> */}

                    <Tooltip title='Previous Page' arrow>
                        <IconButton
                            onClick={() => navigate('/checkouttab')}
                            sx={{
                                backgroundColor: '#635bff',
                                borderRadius: '50%',
                                width: { xs: 32, sm: 35 },
                                height: { xs: 32, sm: 35 },
                                '&:hover': {
                                    backgroundColor: '#635bff',
                                },
                                boxShadow: 3,
                            }}
                        >
                            <ArrowBackIcon sx={{
                                color: 'white',
                                fontSize: { xs: '1rem', sm: '1.25rem' }
                            }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            <Box
                className={`table-grid ag-theme-alpine ${isMobile ? 'ag-grid-mobile' : ''}`}
                sx={{
                    height: { xs: 'calc(97% - 80px)', sm: 'calc(97% - 56px)' },
                    width: '95%',
                    margin: '0 auto',
                    position: 'relative',
                    '&.ag-grid-mobile .ag-cell': {
                        fontSize: '12px',
                        padding: '0 4px',
                    },
                    '&.ag-grid-mobile .ag-header-cell-text': {
                        fontSize: '11px',
                        fontWeight: 'bold',
                    }
                }}
            >
                {isLoading ? (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 10,
                            textAlign: 'center',
                        }}
                    >
                        <CircularProgress size="3rem" sx={{ color: '#635bff' }} />
                        <Typography sx={{
                            marginTop: 2,
                            color: '#635bff',
                            fontWeight: 'bold',
                            fontSize: { xs: '0.875rem', sm: '1rem' }
                        }}>
                            Loading data...
                        </Typography>
                    </Box>
                ) : (
                    <AgGridReact
                        ref={gridRef}
                        rowData={rowData}
                        columnDefs={colDefs}
                        defaultColDef={defaultColDef}
                        pagination={true}
                        paginationPageSize={isMobile ? 20 : 1000}
                        onRowDoubleClicked={handleRowDoubleClick}
                        paginationPageSizeSelector={isMobile ? [20, 50, 100] : [100, 500, 1000, 5000]}
                        onGridReady={onGridReady}
                        onGridSizeChanged={onGridSizeChanged}
                        enableCellTextSelection={true}
                        rowHeight={isMobile ? 30 : 25}
                        headerHeight={isMobile ? 35 : 30}
                        floatingFiltersHeight={28}
                        suppressHorizontalScroll={false}
                        domLayout={isMobile ? "autoHeight" : "normal"}
                        overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">No rows to show</span>'}
                        tooltipShowDelay={0}
                        suppressColumnVirtualisation={isMobile}
                    />
                )}
            </Box>
            <Box
                sx={{
                    position: 'absolute',
                    bottom: { xs: 8, sm: 16 },
                    left: { xs: 20, sm: 70 },
                    fontSize: { xs: '12px', sm: '14px' },
                    color: 'black',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    padding: { xs: '2px 8px', sm: '4px 12px' },
                    borderRadius: '4px',
                }}
            >
                Total Records: <b>{rowData.length}</b>
            </Box>
        </Box>
    );
};

export default CheckoutApprovTable;